export type DifficultyLevel = 'recruit' | 'veteran' | 'trooper' | 'bug_planet';

export interface DifficultyConfig {
  name: string;
  displayName: string;
  description: string;
  lives: number;
  spawnRateMultiplier: number; // 1.0 = baseline, 0.7 = 30% slower
  hasRespawnInvincibility: boolean;
  freeStartingUpgrade: boolean;
  hasSporeClouds: boolean;
  locked: boolean;
}

const LOCAL_STORAGE_KEYS = {
  DIFFICULTY: 'hivejump_difficulty',
  EXPERT_CLEARED: 'hivejump_expert_cleared',
} as const;

export const DIFFICULTY_CONFIGS: Record<DifficultyLevel, DifficultyConfig> = {
  recruit: {
    name: 'recruit',
    displayName: 'Recruit',
    description: 'Beginner - 3 lives, slower spawns',
    lives: 3,
    spawnRateMultiplier: 0.7, // 30% slower than veteran
    hasRespawnInvincibility: true,
    freeStartingUpgrade: false,
    hasSporeClouds: false,
    locked: false,
  },
  veteran: {
    name: 'veteran',
    displayName: 'Veteran',
    description: 'Intermediate - standard experience',
    lives: 1,
    spawnRateMultiplier: 1.0, // baseline
    hasRespawnInvincibility: false,
    freeStartingUpgrade: false,
    hasSporeClouds: false,
    locked: false,
  },
  trooper: {
    name: 'trooper',
    displayName: 'Trooper',
    description: 'Expert - free upgrade at start',
    lives: 1,
    spawnRateMultiplier: 1.0,
    hasRespawnInvincibility: false,
    freeStartingUpgrade: true,
    hasSporeClouds: false,
    locked: false,
  },
  bug_planet: {
    name: 'bug_planet',
    displayName: 'Bug Planet',
    description: 'Impossible - toxic spore clouds',
    lives: 1,
    spawnRateMultiplier: 1.0,
    hasRespawnInvincibility: false,
    freeStartingUpgrade: false,
    hasSporeClouds: true,
    locked: true, // Locked by default
  },
};

export class DifficultySystem {
  private currentDifficulty: DifficultyLevel = 'veteran';

  constructor() {
    // Load saved difficulty preference
    const saved = localStorage.getItem(LOCAL_STORAGE_KEYS.DIFFICULTY);
    if (saved && this.isValidDifficulty(saved)) {
      this.currentDifficulty = saved as DifficultyLevel;
    }
  }

  private isValidDifficulty(value: string): value is DifficultyLevel {
    return ['recruit', 'veteran', 'trooper', 'bug_planet'].includes(value);
  }

  getCurrentDifficulty(): DifficultyLevel {
    return this.currentDifficulty;
  }

  getCurrentConfig(): DifficultyConfig {
    const config = DIFFICULTY_CONFIGS[this.currentDifficulty];
    // Check if bug_planet should be unlocked
    if (this.currentDifficulty === 'bug_planet' && !this.isBugPlanetUnlocked()) {
      return { ...config, locked: true };
    }
    return config;
  }

  setDifficulty(level: DifficultyLevel): boolean {
    // Prevent setting bug_planet if locked
    if (level === 'bug_planet' && !this.isBugPlanetUnlocked()) {
      return false;
    }
    
    this.currentDifficulty = level;
    localStorage.setItem(LOCAL_STORAGE_KEYS.DIFFICULTY, level);
    return true;
  }

  isBugPlanetUnlocked(): boolean {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERT_CLEARED) === 'true';
  }

  unlockBugPlanet(): void {
    localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERT_CLEARED, 'true');
  }

  getAllDifficulties(): { level: DifficultyLevel; config: DifficultyConfig; locked: boolean }[] {
    const bugPlanetUnlocked = this.isBugPlanetUnlocked();
    return (Object.keys(DIFFICULTY_CONFIGS) as DifficultyLevel[]).map((level) => {
      const config = DIFFICULTY_CONFIGS[level];
      const isLocked = config.locked && level === 'bug_planet' && !bugPlanetUnlocked;
      return { level, config, locked: isLocked };
    });
  }

  // Static helper to check if expert was cleared (for external use)
  static isExpertCleared(): boolean {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERT_CLEARED) === 'true';
  }

  // Static helper to get the saved difficulty
  static getSavedDifficulty(): DifficultyLevel | null {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEYS.DIFFICULTY);
    if (saved && ['recruit', 'veteran', 'trooper', 'bug_planet'].includes(saved)) {
      return saved as DifficultyLevel;
    }
    return null;
  }
}
